import {
  ThunkAction,
  UnknownAction,
  createSlice,
  createSelector,
  PayloadAction
} from '@reduxjs/toolkit';

import { RootState } from '../../../store';
import { loadFields } from '../../accordion/accordionSlice';
import {
  apiSlice,
  loadProperties,
  resetItem,
  selectItemProperties,
  selectProductTag,
  setProductId,
  setProductTag,
  setProductVdpCode,
  updateStyle
} from '../../item/store/itemSlice';

import {
  ConfigSnapshot,
  EditSessionLinkedUpsell,
  MultiConfigSliceState
} from '../types';
import { getConfigNameValue } from '../utils';

const initialState: MultiConfigSliceState = {
  enabled: false,
  maxConfigs: null,
  minConfigs: 1,
  configIds: [],
  configsById: {},
  activeConfigId: null,
  submittedCartItemsByConfigId: {},
  editSessionShopifyKeysByConfigId: {},
  editSessionMultiConfigSessionId: null,
  editSessionUpsellsByConfigId: {},
  invalidConfigIds: []
};

export function generateConfigId() {
  return crypto.randomUUID();
}

// Reads the current editor state (both the "official" item properties and
// the in-progress text field values) as one restorable snapshot.
export function captureCurrentSnapshot(state: RootState): ConfigSnapshot {
  return {
    properties: selectItemProperties(state),
    textFields: state.accordion.fields
  };
}

export const multiConfigSlice = createSlice({
  name: 'multiConfig',
  initialState,
  reducers: {
    initSession: (
      state,
      action: PayloadAction<{
        id: string;
        snapshot: ConfigSnapshot;
        maxConfigs: number | null;
        minConfigs: number;
      }>
    ) => {
      const { id, snapshot, maxConfigs, minConfigs } = action.payload;
      state.enabled = true;
      state.maxConfigs = maxConfigs;
      state.minConfigs = minConfigs;
      state.configIds = [id];
      state.configsById = { [id]: snapshot };
      state.activeConfigId = id;
      state.invalidConfigIds = [];
    },
    resetSession: () => initialState,
    addConfig: (
      state,
      action: PayloadAction<{ id: string; snapshot: ConfigSnapshot }>
    ) => {
      const { id, snapshot } = action.payload;
      state.configIds.push(id);
      state.configsById[id] = snapshot;
      state.activeConfigId = id;
    },
    saveActiveConfig: (
      state,
      action: PayloadAction<{ id: string; snapshot: ConfigSnapshot }>
    ) => {
      state.configsById[action.payload.id] = action.payload.snapshot;
    },
    removeConfig: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.configIds = state.configIds.filter(configId => configId !== id);
      delete state.configsById[id];
    },
    setActiveConfigId: (state, action: PayloadAction<string>) => {
      state.activeConfigId = action.payload;
    },
    setSubmittedCartItems: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.submittedCartItemsByConfigId = action.payload;
    },
    flagInvalidConfigIds: (state, action: PayloadAction<string[]>) => {
      state.invalidConfigIds = action.payload;
    },
    clearInvalidConfigIds: state => {
      state.invalidConfigIds = [];
    },
    // Populates the whole session at once from reconstructed edit data,
    // rather than starting from a single seed configuration - see
    // reconstructMultiConfigSessionForEdit.
    hydrateSessionFromEdit: (
      state,
      action: PayloadAction<{
        configs: Array<{ id: string; snapshot: ConfigSnapshot }>;
        activeConfigId: string;
        maxConfigs: number | null;
        minConfigs: number;
        shopifyKeysByConfigId: Record<string, string>;
        cartItemIdsByConfigId: Record<string, string>;
        multiConfigSessionId: string;
        upsellsByConfigId: Record<string, EditSessionLinkedUpsell[]>;
      }>
    ) => {
      const {
        configs,
        activeConfigId,
        maxConfigs,
        minConfigs,
        shopifyKeysByConfigId,
        cartItemIdsByConfigId,
        multiConfigSessionId,
        upsellsByConfigId
      } = action.payload;
      state.enabled = true;
      state.maxConfigs = maxConfigs;
      state.minConfigs = minConfigs;
      state.configIds = configs.map(config => config.id);
      state.configsById = Object.fromEntries(
        configs.map(config => [config.id, config.snapshot])
      );
      state.activeConfigId = activeConfigId;
      state.editSessionShopifyKeysByConfigId = shopifyKeysByConfigId;
      state.submittedCartItemsByConfigId = cartItemIdsByConfigId;
      state.editSessionMultiConfigSessionId = multiConfigSessionId;
      state.editSessionUpsellsByConfigId = upsellsByConfigId;
      state.invalidConfigIds = [];
    }
  }
});

const {
  initSession,
  resetSession,
  addConfig,
  saveActiveConfig,
  removeConfig,
  setActiveConfigId,
  setSubmittedCartItems,
  hydrateSessionFromEdit,
  flagInvalidConfigIds,
  clearInvalidConfigIds
} = multiConfigSlice.actions;

export {
  setSubmittedCartItems,
  hydrateSessionFromEdit,
  setActiveConfigId,
  flagInvalidConfigIds,
  clearInvalidConfigIds
};

export { resetSession };

export function loadSnapshot(
  snapshot: ConfigSnapshot
): ThunkAction<void, RootState, unknown, UnknownAction> {
  return dispatch => {
    dispatch(loadProperties(snapshot.properties));
    dispatch(loadFields(snapshot.textFields));
  };
}

// Seeds the session with the configuration currently loaded in the editor
// as the first entry. Call once a multi-config-enabled product has finished
// loading its initial style/palette/etc. defaults.
export const startMultiConfigSession =
  (
    maxConfigs: number | null,
    minConfigs: number
  ): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const snapshot = captureCurrentSnapshot(getState());
    dispatch(
      initSession({ id: generateConfigId(), snapshot, maxConfigs, minConfigs })
    );
  };

// Resets the editor to the product's own defaults and adds that as a new
// configuration, making it active - a genuinely new person, not a copy of
// whoever was just being edited (see duplicateConfig for that). Reuses
// updateStyle - the same "apply this style, cascading first-available
// palette/font/icon/label shape/slots/material for anything not already
// valid" thunk the product's own initial load (Configurator.tsx's
// handleShowReactApp) relies on to pick its own defaults - rather than
// re-deriving what "default" means for this product, which would drift out
// of sync with the real thing over time.
export const configureAnother =
  (): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const state = getState();
    const { activeConfigId } = state.multiConfig;

    // Guards against a race where this fires before startMultiConfigSession
    // has run (e.g. a click landing in the gap before that effect fires) -
    // without this, addConfig would push onto an empty list, and the
    // session-start effect would then overwrite it via initSession.
    if (!activeConfigId) return;

    if (selectIsAtConfigCap(state)) return;

    const currentSnapshot = captureCurrentSnapshot(state);

    // Cloning an unnamed configuration would just produce a second unnamed
    // one - mirrors the disabled state ConfigureAnotherButton already shows.
    if (!getConfigNameValue(currentSnapshot.properties)?.trim()) return;

    dispatch(
      saveActiveConfig({ id: activeConfigId, snapshot: currentSnapshot })
    );

    const productTag = selectProductTag(state);
    const apiProduct =
      apiSlice.endpoints.getProduct.select(productTag)(state)?.data;

    // resetItem() blanks productId/productTag/vdpcode along with everything
    // else, so they have to be restored - the product itself isn't
    // changing, only this configuration's choices within it.
    dispatch(resetItem());
    dispatch(loadFields({}));
    dispatch(setProductTag(productTag));

    if (apiProduct) {
      dispatch(setProductId(apiProduct.product_id));
      dispatch(setProductVdpCode(apiProduct.vdpcode ?? null));

      const [firstStyle] = apiProduct.styles ?? [];
      if (firstStyle) {
        // Awaited - updateStyle has an async branch (fetching a default
        // icon), and the snapshot below needs to reflect its result, not
        // whatever was set (or still unset) before it resolves.
        await dispatch(updateStyle(firstStyle));
      }
    }

    const defaultSnapshot = captureCurrentSnapshot(getState());
    dispatch(addConfig({ id: generateConfigId(), snapshot: defaultSnapshot }));
  };

// Clones an existing configuration (by id, not necessarily the active one)
// into a new one, name included, and makes it active - for shoppers who
// want an exact copy (e.g. siblings sharing a last name) rather than a
// blank-named new entry. Mirrors configureAnother's guards (cap, source
// must be named) and switchConfig's "save the outgoing active config first"
// step, since duplicating a non-active row still means switching away from
// whatever's currently being edited.
export const duplicateConfig =
  (sourceId: string): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { activeConfigId, configsById } = state.multiConfig;

    if (selectIsAtConfigCap(state)) return;

    const currentSnapshot = captureCurrentSnapshot(state);
    const sourceSnapshot =
      sourceId === activeConfigId ? currentSnapshot : configsById[sourceId];
    if (!sourceSnapshot) return;

    if (!getConfigNameValue(sourceSnapshot.properties)?.trim()) return;

    if (activeConfigId) {
      dispatch(
        saveActiveConfig({ id: activeConfigId, snapshot: currentSnapshot })
      );
    }

    dispatch(addConfig({ id: generateConfigId(), snapshot: sourceSnapshot }));
    dispatch(loadSnapshot(sourceSnapshot));
  };

// Saves the editor's current values back into the active configuration's
// slot, then loads the target configuration's saved values into the editor.
export const switchConfig =
  (targetId: string): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { activeConfigId, configsById } = state.multiConfig;

    if (targetId === activeConfigId) return;

    const targetSnapshot = configsById[targetId];
    if (!targetSnapshot) return;

    const currentSnapshot = captureCurrentSnapshot(state);
    if (activeConfigId) {
      dispatch(
        saveActiveConfig({ id: activeConfigId, snapshot: currentSnapshot })
      );
    }

    dispatch(setActiveConfigId(targetId));
    dispatch(loadSnapshot(targetSnapshot));
  };

// Removes a configuration from the session. Refuses to delete below the
// product's configured minimum. If the deleted configuration was active, the
// first remaining configuration becomes active and is loaded into the
// editor.
export const deleteConfig =
  (id: string): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { configIds, activeConfigId, minConfigs } = state.multiConfig;

    if (configIds.length <= minConfigs) return;

    const wasActive = id === activeConfigId;
    dispatch(removeConfig(id));

    if (wasActive) {
      const nextState = getState();
      const newActiveId = nextState.multiConfig.configIds[0];
      const newActiveSnapshot = nextState.multiConfig.configsById[newActiveId];
      dispatch(setActiveConfigId(newActiveId));
      dispatch(loadSnapshot(newActiveSnapshot));
    }
  };

// Guarded with `?.` because Configurator.tsx (where this is read
// unconditionally) is also rendered by partner-embed, whose store doesn't
// register a `multiConfig` reducer at all - multi-config should simply be
// inert there, not crash.
export const selectMultiConfigEnabled = (state: RootState) =>
  state.multiConfig?.enabled ?? false;

export const selectConfigIds = (state: RootState) =>
  state.multiConfig?.configIds ?? [];

export const selectConfigsById = (state: RootState) =>
  state.multiConfig?.configsById ?? {};

// Resolves every configuration in session order to its effective snapshot -
// the active configuration reads live item/accordion state (since its
// configsById entry only refreshes on switch/delete/configure-another),
// every other configuration reads its stored snapshot. Shared by the
// switcher UI and the multi-config add-to-cart flow so both agree on what
// each configuration's "current" values actually are.
// Memoized (createSelector) rather than a plain function - this is also
// used as an effect dependency (upsell availability, keyed off config #1),
// and a plain function would return a new array identity on every render,
// causing that effect to re-fire and wipe chosen upsells continuously.
export const selectAllConfigSnapshots = createSelector(
  [
    selectConfigIds,
    selectConfigsById,
    (state: RootState) => state.multiConfig?.activeConfigId ?? null,
    selectItemProperties,
    (state: RootState) => state.accordion.fields
  ],
  (
    configIds,
    configsById,
    activeConfigId,
    liveProperties,
    liveTextFields
  ): Array<{ id: string } & ConfigSnapshot> => {
    const liveSnapshot: ConfigSnapshot = {
      properties: liveProperties,
      textFields: liveTextFields
    };

    return configIds.map(id =>
      id === activeConfigId
        ? { id, ...liveSnapshot }
        : { id, ...configsById[id] }
    );
  }
);

// The session's "primary" configuration (#1) - the one full-price items,
// upsells, and _parent_item_id linkage all anchor to.
export const selectFirstConfigSnapshot = createSelector(
  [selectAllConfigSnapshots],
  snapshots => snapshots[0] as ({ id: string } & ConfigSnapshot) | undefined
);

// The configuration currently loaded in the editor.
export const selectActiveConfigSnapshot = createSelector(
  [
    selectAllConfigSnapshots,
    (state: RootState) => state.multiConfig?.activeConfigId ?? null
  ],
  (snapshots, activeConfigId) =>
    snapshots.find(snapshot => snapshot.id === activeConfigId)
);

export const selectSubmittedCartItemsByConfigId = (state: RootState) =>
  state.multiConfig?.submittedCartItemsByConfigId ?? {};

export const selectEditSessionShopifyKeysByConfigId = (state: RootState) =>
  state.multiConfig?.editSessionShopifyKeysByConfigId ?? {};

export const selectEditSessionMultiConfigSessionId = (state: RootState) =>
  state.multiConfig?.editSessionMultiConfigSessionId ?? null;

export const selectEditSessionUpsellsByConfigId = (state: RootState) =>
  state.multiConfig?.editSessionUpsellsByConfigId ?? {};

// True only when this session was reconstructed from an existing cart line
// (as opposed to a brand new, not-yet-submitted session) - the re-save flow
// uses this to decide whether to update/remove existing Shopify lines
// rather than creating a whole new session.
export const selectIsEditingMultiConfigSession = (state: RootState) =>
  Object.keys(state.multiConfig?.editSessionShopifyKeysByConfigId ?? {})
    .length > 0;

export const selectActiveConfigId = (state: RootState) =>
  state.multiConfig?.activeConfigId ?? null;

export const selectMaxConfigs = (state: RootState) =>
  state.multiConfig?.maxConfigs ?? null;

export const selectMinConfigs = (state: RootState) =>
  state.multiConfig?.minConfigs ?? 1;

export const selectIsAtConfigCap = (state: RootState) => {
  const max = selectMaxConfigs(state);
  return max !== null && selectConfigIds(state).length >= max;
};

// True when the session has fewer configurations than the product's
// configured minimum - used to block add-to-cart/checkout until the shopper
// adds enough configurations.
export const selectIsBelowConfigMinimum = (state: RootState) =>
  selectConfigIds(state).length < selectMinConfigs(state);

export const selectCanDeleteConfig = (state: RootState) =>
  selectConfigIds(state).length > selectMinConfigs(state);

export const selectInvalidConfigIds = (state: RootState) =>
  state.multiConfig?.invalidConfigIds ?? [];

export default multiConfigSlice.reducer;
